import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import * as recordings from '../recordings/recordingsApi'
import type { RecordingExport } from '../recordings/recordingsApi'
import { ApiError } from '../../shared/api/http'
import { localDateTime, TransmissionDateTimePicker } from './TransmissionDateTimePicker'

type SavedVideo = { file: File; url: string }
const dateLabel = (value: string) => value ? new Date(value).toLocaleDateString('pt-BR') : 'Escolher data'
const timeLabel = (value: string) => value ? new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--:--:--'
const sendDownload = (video: SavedVideo) => {
  const anchor = document.createElement('a')
  anchor.href = video.url; anchor.download = video.file.name; anchor.click()
}
const sizeLabel = (bytes: number) => `${(bytes / 1048576).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`
const exportError = (code: string | null) => {
  switch (code) {
    case 'recording_unavailable': return 'Não há gravação disponível nesse período.'
    case 'export_too_large': return 'O vídeo ultrapassa o limite de 1 GB. Escolha um período menor.'
    case 'invalid_export_period': return 'Escolha um período válido, de até 1 hora e sem horários futuros.'
    case 'forbidden': return 'Você não tem acesso à gravação desta câmera.'
    case 'export_queue_full': return 'Há muitas exportações em andamento. Tente novamente em instantes.'
    default: return 'Não foi possível preparar este MP4. Tente novamente.'
  }
}

type Props = { cameraId: string; cameraName: string; initialMoment: Date; open: boolean; onClose: () => void }

export function TransmissionExportDialog({ cameraId, cameraName, initialMoment, open, onClose }: Props) {
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [job, setJob] = useState<RecordingExport | null>(null)
  const [creating, setCreating] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [progress, setProgress] = useState<{ received: number; total: number | null } | null>(null)
  const [error, setError] = useState('')
  const [pollFailed, setPollFailed] = useState(false)
  const [pollAttempt, setPollAttempt] = useState(0)
  const [downloaded, setDownloaded] = useState(false)
  const [saved, setSaved] = useState<SavedVideo | null>(null)
  const [sharing, setSharing] = useState(false)
  const [notice, setNotice] = useState('')
  const [editingField, setEditingField] = useState<'start' | 'end' | null>(null)
  const savedRef = useRef<SavedVideo | null>(null)
  const revision = useRef(0)
  const downloadController = useRef<AbortController | null>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const initial = useRef(initialMoment)
  initial.current = initialMoment
  const working = creating || downloading || sharing
  const preparing = Boolean(job && job.state !== 'ready' && job.state !== 'failed')

  useEffect(() => () => {
    revision.current++; downloadController.current?.abort()
    if (savedRef.current) {
      const url = savedRef.current.url
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
    }
  }, [])
  useEffect(() => {
    if (!open) return
    if (!start) {
      const now = Date.now()
      const begins = Math.min(initial.current.getTime(), now - 1000)
      setStart(localDateTime(new Date(begins)))
      setEnd(localDateTime(new Date(Math.min(now, begins + 300_000))))
    }
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    dialogRef.current?.focus()
    return () => { if (previous?.isConnected) previous.focus() }
  }, [open])

  useEffect(() => {
    if (!open || !job || job.state === 'ready' || job.state === 'failed') return
    let active = true
    let timer: number | undefined
    const id = job.id
    const poll = async () => {
      try {
        const current = await recordings.exportStatus(id)
        if (!active) return
        setJob(current)
        setPollFailed(false)
        if (current.state === 'failed') setError(exportError(current.errorCode))
        else if (current.state !== 'ready') timer = window.setTimeout(() => void poll(), 2000)
      } catch (failure) {
        if (!active) return
        if (failure instanceof ApiError && (failure.status === 404 || failure.status === 410)) {
          setJob(null)
          setPollFailed(false)
          setError('Esta exportação expirou ou não está mais disponível. Prepare o MP4 novamente.')
          return
        }
        setPollFailed(true)
        setError('Não foi possível atualizar o progresso. Tente consultar novamente.')
      }
    }
    setPollFailed(false)
    void poll()
    return () => { active = false; if (timer !== undefined) window.clearTimeout(timer) }
  }, [open, job?.id, pollAttempt])

  function clearSaved() {
    if (savedRef.current) {
      const url = savedRef.current.url
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
    }
    savedRef.current = null; setSaved(null); setDownloaded(false); setNotice('')
  }

  function changePeriod(value: string) {
    if (editingField === 'start') setStart(value)
    else setEnd(value)
    setJob(null); setError(''); clearSaved()
  }

  async function prepare(event: FormEvent) {
    event.preventDefault()
    if (working || preparing) return
    const begins = new Date(start), finishes = new Date(end)
    if (!start || !end || !Number.isFinite(begins.getTime()) || !Number.isFinite(finishes.getTime()) ||
      finishes <= begins || finishes.getTime() > Date.now() || finishes.getTime() - begins.getTime() > 3_600_000) {
      setError(exportError('invalid_export_period')); return
    }
    const request = ++revision.current
    setCreating(true); setError(''); setJob(null); clearSaved(); setEditingField(null)
    try {
      const created = await recordings.createExport(cameraId, begins, finishes)
      if (revision.current !== request) return
      setJob(created)
      if (created.state === 'failed') setError(exportError(created.errorCode))
    } catch (failure) {
      if (revision.current === request) setError(exportError(failure instanceof ApiError ? failure.code : null))
    } finally { if (revision.current === request) setCreating(false) }
  }

  async function download() {
    if (!job || job.state !== 'ready' || working) return
    if (saved) { sendDownload(saved); setDownloaded(true); return }
    const request = ++revision.current
    const controller = new AbortController()
    downloadController.current = controller
    let timedOut = false
    let inactivity: number | undefined
    const armTimeout = () => {
      if (inactivity !== undefined) window.clearTimeout(inactivity)
      inactivity = window.setTimeout(() => { timedOut = true; controller.abort() }, 45_000)
    }
    armTimeout()
    setDownloading(true); setError(''); setDownloaded(false)
    setProgress({ received: 0, total: job.fileSizeBytes })
    try {
      const blob = await recordings.exportFileWithProgress(job.id, (received, total) => {
        armTimeout()
        if (revision.current === request) setProgress({ received, total: total ?? job.fileSizeBytes })
      }, controller.signal)
      if (revision.current !== request) return
      const file = new File([blob], job.fileName ?? `gravacao-${start.slice(0, 10)}.mp4`, { type: 'video/mp4' })
      const video = { file, url: URL.createObjectURL(file) }
      savedRef.current = video; setSaved(video)
      sendDownload(video)
      setDownloaded(true)
    } catch {
      if (revision.current === request) setError(timedOut ? 'O download parou de responder. Verifique a conexão e tente baixar novamente.' : 'Não foi possível baixar o MP4. O arquivo pode ter expirado; tente novamente ou prepare outro período.')
    } finally {
      if (inactivity !== undefined) window.clearTimeout(inactivity)
      if (downloadController.current === controller) downloadController.current = null
      if (revision.current === request) { setDownloading(false); setProgress(null) }
    }
  }

  function openVideo() {
    if (!saved) return
    // A separate URL keeps an opened video valid when this dialog changes.
    const url = URL.createObjectURL(saved.file)
    const tab = window.open(url, '_blank')
    if (!tab) { URL.revokeObjectURL(url); setNotice('Permita a abertura da nova aba ou use o MP4 baixado.'); return }
    tab.opener = null
    const cleanup = window.setInterval(() => {
      if (tab.closed) { window.clearInterval(cleanup); URL.revokeObjectURL(url) }
    }, 1000)
  }

  async function shareVideo() {
    if (!saved || sharing) return
    setError(''); setNotice('')
    const files = [saved.file]
    if (!navigator.canShare?.({ files }) || !navigator.share) {
      sendDownload(saved)
      setNotice('Use o arquivo baixado para compartilhar pelo WhatsApp ou outro aplicativo neste navegador.')
      return
    }
    const request = revision.current
    setSharing(true)
    try {
      // Keep share directly inside this user gesture, with no request beforehand.
      await navigator.share({ files })
    } catch (failure) {
      if (revision.current === request && !(failure instanceof DOMException && failure.name === 'AbortError')) {
        setNotice('Não foi possível abrir o compartilhamento. Você pode usar o arquivo baixado.')
      }
    } finally { if (revision.current === request) setSharing(false) }
  }

  function close() {
    if (creating || sharing) return
    if (downloadController.current) {
      revision.current++
      downloadController.current.abort()
      downloadController.current = null
      setDownloading(false); setProgress(null)
    }
    setEditingField(null); onClose()
  }

  function handleKeys(event: KeyboardEvent<HTMLElement>) {
    if (event.key === 'Escape' && !creating && !sharing) { event.preventDefault(); close(); return }
    if (event.key !== 'Tab') return
    const controls = [...(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), [tabindex="0"]') ?? [])]
      .filter(control => control.tabIndex >= 0 && control.getClientRects().length > 0)
    if (!controls.length) { event.preventDefault(); return }
    const first = controls[0], last = controls[controls.length - 1]
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
  }

  if (!open) return null
  const percent = progress?.total ? Math.min(100, Math.round(progress.received / progress.total * 100)) : null
  const preparationPercent = Math.max(0, Math.min(100, job?.progressPercent ?? 0))
  return createPortal(<div className="transmission-export-backdrop" onClick={event => { if (event.target === event.currentTarget && !creating) close() }}>
    <section className="transmission-export-dialog" role="dialog" aria-modal="true" aria-labelledby="transmission-export-title" tabIndex={-1} ref={dialogRef} onKeyDown={handleKeys}>
      <header><span className="transmission-export-icon" aria-hidden="true">↓</span><div><small>GRAVAÇÕES DA CÂMERA</small><h2 id="transmission-export-title">Download MP4</h2><p>{cameraName}</p></div><button type="button" className="transmission-export-close" aria-label="Fechar download" disabled={creating || sharing} onClick={close}>×</button></header>
      <form className={editingField ? 'editing-period' : undefined} onSubmit={event => void prepare(event)}>
        <p className="transmission-export-description">Exporta as gravações disponíveis no período. Escolha o início e o término; o MP4 mantém a velocidade original.</p>
        <div className="transmission-export-period">
          {([{ key: 'start', label: 'Início', value: start }, { key: 'end', label: 'Término', value: end }] as const).map(field => <div className="transmission-period-field" key={field.key}><span>{field.label}</span><button type="button" aria-label={`Alterar ${field.label.toLowerCase()}`} aria-expanded={editingField === field.key} disabled={working || preparing} onClick={() => setEditingField(current => current === field.key ? null : field.key)}><span aria-hidden="true">▦</span><span><strong>{dateLabel(field.value)}</strong><small>{timeLabel(field.value)}</small></span><span aria-hidden="true">⌄</span></button></div>)}
        </div>
        {editingField && <TransmissionDateTimePicker key={editingField} label={editingField === 'start' ? 'Início' : 'Término'} value={editingField === 'start' ? start : end} onChange={changePeriod} onClose={() => setEditingField(null)}/>}
        <small className="transmission-export-limit">Horário local · até 1 hora por arquivo</small>
        {(creating || job) && <div className={`transmission-export-progress ${job?.state ?? 'preparing'}`} role="status" aria-live="polite">
          <div><strong>{downloading ? 'Baixando MP4…' : downloaded ? 'Download enviado ao navegador' : job?.state === 'ready' ? 'MP4 pronto para baixar' : job?.state === 'failed' ? 'Não foi possível preparar' : job?.state === 'queued' ? 'Aguardando preparação…' : 'Preparando MP4…'}</strong><span>{downloading ? percent === null ? sizeLabel(progress?.received ?? 0) : `${percent}%` : job?.state === 'ready' ? job.fileSizeBytes ? sizeLabel(job.fileSizeBytes) : '' : job?.state === 'failed' ? '' : `${preparationPercent}%`}</span></div>
          {(creating || preparing || downloading) && <progress max="100" value={downloading ? percent ?? undefined : preparationPercent} aria-label={downloading ? 'Progresso do download' : 'Progresso da preparação'}/>}
        </div>}
        {error && <p className="transmission-export-error" role="alert">{error}</p>}
        {notice && <p className="transmission-export-notice" role="status">{notice}</p>}
        {downloaded && saved && <div className="transmission-export-file-actions"><button type="button" onClick={openVideo} disabled={sharing}><span aria-hidden="true">▶</span>Abrir vídeo</button><button type="button" onClick={() => void shareVideo()} disabled={sharing} title="Escolha WhatsApp ou outro aplicativo"><span aria-hidden="true">↗</span>{sharing ? 'Abrindo…' : 'Compartilhar'}</button></div>}
        {pollFailed && <button type="button" className="transmission-export-retry" onClick={() => { setError(''); setPollAttempt(value => value + 1) }}>Atualizar progresso</button>}
        <footer><button type="button" className="transmission-export-secondary" disabled={creating || sharing} onClick={close}>{downloading ? 'Cancelar download' : 'Fechar'}</button>{job?.state === 'ready' ? <button type="button" className="transmission-export-primary" disabled={working} onClick={() => void download()}>{downloading ? 'Baixando…' : downloaded ? 'Baixar novamente' : '↓ Baixar MP4'}</button> : <button className="transmission-export-primary" type="submit" disabled={working || preparing || Boolean(editingField)}>{creating || preparing ? 'Preparando…' : 'Preparar MP4'}</button>}</footer>
      </form>
    </section>
  </div>, document.fullscreenElement ?? document.body)
}
